import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';

/**
 * Hook personalizzato per gestire i gruppi muscolari
 * Fornisce funzioni per recuperare, filtrare e gestire i muscoli
 */
const useMuscleGroups = () => {
  const { getAuthenticatedApi } = useAuth();
  const [muscles, setMuscles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  /**
   * Recupera tutti i gruppi muscolari dall'API
   * @param {string} category - Categoria opzionale per filtrare (es: 'Gambe', 'Petto')
   * @param {boolean} activeOnly - Se true, recupera solo muscoli attivi
   * @param {boolean} groupByCategory - Se true, raggruppa i risultati per categoria
   */
  const fetchMuscles = useCallback(async (category = null, activeOnly = true, groupByCategory = false) => {
    setLoading(true);
    setError(null);

    try {
      const api = getAuthenticatedApi();
      let url = '/muscle_groups.php';
      const params = new URLSearchParams();
      
      if (category) params.append('category', category);
      if (activeOnly !== undefined) params.append('active_only', activeOnly);
      if (groupByCategory) params.append('group_by_category', groupByCategory);
      
      const queryString = params.toString();
      if (queryString) url += `?${queryString}`;

      const response = await api.get(url);
      
      if (response.data.success) {
        setMuscles(response.data.data);
        return response.data.data;
      } else {
        throw new Error(response.data.message || 'Errore nel recupero dei muscoli');
      }
    } catch (err) {
      console.error('Errore nel recupero dei muscoli:', err);
      setError(err.message);
      return [];
    } finally {
      setLoading(false);
    }
  }, [getAuthenticatedApi]);

  /**
   * Recupera i muscoli al mount del componente
   */
  useEffect(() => {
    fetchMuscles();
  }, [fetchMuscles]);

  /**
   * Filtra i muscoli per categoria
   * @param {string} category - Categoria da filtrare (es: 'Gambe', 'Petto')
   * @returns {Array} - Array di muscoli filtrati
   */
  const getMusclesByCategory = useCallback((category) => {
    if (!category) return muscles;
    return muscles.filter(m => m.parent_category === category);
  }, [muscles]);

  /**
   * Trova un muscolo per ID
   * @param {number} id - ID del muscolo
   * @returns {object|null} - Oggetto muscolo o null se non trovato
   */
  const getMuscleById = useCallback((id) => {
    return muscles.find(m => m.id === parseInt(id)) || null;
  }, [muscles]);

  /**
   * Trova muscoli per array di ID
   * @param {Array<number>} ids - Array di ID muscoli
   * @returns {Array} - Array di oggetti muscolo
   */
  const getMusclesByIds = useCallback((ids) => {
    if (!ids || !Array.isArray(ids)) return [];
    return ids.map(id => getMuscleById(id)).filter(Boolean);
  }, [getMuscleById]);

  /**
   * Ottiene tutte le categorie uniche
   * @returns {Array<string>} - Array di nomi categorie
   */
  const getCategories = useCallback(() => {
    const categories = muscles.map(m => m.parent_category).filter(Boolean);
    return [...new Set(categories)].sort();
  }, [muscles]);

  /**
   * Raggruppa i muscoli per categoria
   * @returns {Object} - Oggetto con categorie come chiavi e array di muscoli come valori
   */
  const getMusclesGroupedByCategory = useCallback(() => {
    const grouped = {};
    muscles.forEach(muscle => {
      const category = muscle.parent_category || 'Altro';
      if (!grouped[category]) {
        grouped[category] = [];
      }
      grouped[category].push(muscle);
    });
    return grouped;
  }, [muscles]);

  /**
   * Cerca muscoli per nome
   * @param {string} searchTerm - Termine di ricerca
   * @returns {Array} - Array di muscoli che corrispondono alla ricerca
   */
  const searchMuscles = useCallback((searchTerm) => {
    if (!searchTerm) return muscles;
    
    const term = searchTerm.toLowerCase();
    return muscles.filter(m => 
      m.name.toLowerCase().includes(term) ||
      (m.name_en && m.name_en.toLowerCase().includes(term)) ||
      (m.parent_category && m.parent_category.toLowerCase().includes(term))
    );
  }, [muscles]);

  return {
    muscles,
    loading,
    error,
    fetchMuscles,
    getMusclesByCategory,
    getMuscleById,
    getMusclesByIds,
    getCategories,
    getMusclesGroupedByCategory,
    searchMuscles
  };
};

export default useMuscleGroups;


